import React, { useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'

import { TableUrlPagination } from '../../../../ui'
import { eeiiDataPreparation } from '../../logics'
import { EEII_TABLE_COLUMNS } from '../../constants'
import { useRowSelector } from '../../../loader/hooks'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const EEIITable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const navigate = useNavigate()
  const location = useLocation()
  const role = useSelector((state) => state.global.role)
  const eeiiLoader = useSelector((state) => state.loader.eeii)
  const combos = useSelector((state) => state.combos)

  const data = eeiiLoader?.data?.empresas || []
  const totalEntries = eeiiLoader?.data?.total_empresas
  const search = eeiiLoader?.config?.params
  const loading = eeiiLoader?.loading

  const { selection, addSelection } = useRowSelector({
    identifier: 'eeii',
    selectFilter: (state) => state?.data?.empresas,
    idField: 'empresa_ca_id',
  })

  useEffect(() => {
    if (selection.length) {
      addSelection([])
    }
  }, [])

  const handleGoToDetail = (_, row) => {
    if (!row) return

    const props = {
      pathname: `/eeii/${row.empresa_id}`,
      search: row.empresa_ca_id ? `?ca=${row.empresa_ca_id}` : '',
      state: { prevSearch: location?.search || '' },
    }
    return navigate(props)
  }

  const handleSelectRows = useCallback(
    (rows) => {
      addSelection(rows)
    },
    [addSelection]
  )

  const formattedColumns = useMemo(() => {
    return EEII_TABLE_COLUMNS.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        header: formatMessage({ id: `pages.eeii.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.width,
        maxSize: column.width,
        muiTableBodyCellProps: {
          style: {
            padding: '0 16px', // Default padding
          },
        },
      }

      if (column.field === 'actions') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.actions' }),
          Cell: (props) => <span>Acciones</span>, // Replace with actual actions component if available
          muiTableBodyCellProps: { align: 'center' },
        }
      }

      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return <span>{lookup[value] ?? value ?? '--'}</span>
          },
        }
      }

      return {
        ...baseColumn,
        Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
      }
    })
  }, [combos, formatMessage])

  const processedData = useMemo(() => eeiiDataPreparation(data, { intl }), [data, intl])

  const finalTableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: role === 'e_commerce',
      draggable: false,
    }),
    [role]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={finalTableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
      onRowClick={handleGoToDetail}
    />
  )
}
